import { Stack } from '@mui/material';
import { ChevronDown, ChevronUp } from '@ucr/icons/main';
import React, {
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { classNames } from 'shared/lib/utils/classNames';
import { Button } from 'shared/ui/Button';
import { IconWrapper } from 'shared/ui/IconWrapper';

import cls from './ExpandableList.module.scss';

interface ExpandableListProps {
  /** Количество элементов в списке (всего) */
  itemsLength: number;
  children: ReactNode;
  /** fallback-высота для старого поведения, если initialItemsCount не задан */
  collapsedHeight?: number;
  /** Максимальная высота раскрытого списка */
  maxExpandedHeight?: number;
  className?: string;
  /** Показывать кнопку раскрытия списка */
  showToggle?: boolean;
  buttonTextReveal?: string;
  buttonTextHide?: string;
  /** Сколько элементов показывать в свернутом состоянии (динамическая высота) */
  initialItemsCount?: number;
  gap?: string;
}

export const ExpandableList: React.FC<ExpandableListProps> = ({
  itemsLength,
  children,
  collapsedHeight = 110,
  maxExpandedHeight = 300,
  className,
  gap,
  showToggle = true,
  buttonTextReveal = 'Развернуть',
  buttonTextHide = 'Скрыть',
  initialItemsCount,
}) => {
  const [expanded, setExpanded] = useState(false);

  // Замеряем высоты
  const [collapsedAutoHeight, setCollapsedAutoHeight] = useState(0); // высота при свернутом состоянии
  const [expandedAutoHeight, setExpandedAutoHeight] = useState(0); // высота при раскрытом состоянии

  const viewRef = useRef<HTMLDivElement>(null); // родительский контейнер

  // скрытые контейнеры для замеров
  const measureCollapsedRef = useRef<HTMLDivElement>(null);
  const measureExpandedRef = useRef<HTMLDivElement>(null);

  // приводим к массиву, чтобы было удобно вычислять необходимое количество отображаемого контента
  const childrenArray = useMemo(
    () => React.Children.toArray(children),
    [children],
  );

  // вычисляем количество отображаемых элементов
  const slicedChildren = useMemo(() => {
    if (!initialItemsCount) return childrenArray;
    return childrenArray.slice(0, initialItemsCount);
  }, [childrenArray, initialItemsCount]);

  // Замеряем высоты после каждого изменения children / initialItemsCount
  useLayoutEffect(() => {
    const collapsedEl = measureCollapsedRef.current; // отображаемые элементы
    const expandedEl = measureExpandedRef.current; // все элементы

    if (collapsedEl) {
      setCollapsedAutoHeight(
        collapsedEl.scrollHeight || collapsedEl.offsetHeight || 0,
      );
    }
    if (expandedEl) {
      setExpandedAutoHeight(
        expandedEl.scrollHeight || expandedEl.offsetHeight || 0,
      );
    }
  }, [childrenArray, slicedChildren, initialItemsCount]);

  // гарант, что раскрытый max-height не меньше свернутого
  const expandedCap = useMemo(() => {
    return Math.max(maxExpandedHeight, collapsedAutoHeight || 0);
  }, [maxExpandedHeight, collapsedAutoHeight]);

  // вычисляем видимую высоту
  const visibleHeight = useMemo(() => {
    if (expanded) {
      return Math.min(expandedAutoHeight || 0, expandedCap);
    }
    if (initialItemsCount) return collapsedAutoHeight || 0;
    return collapsedHeight;
  }, [
    expanded,
    expandedAutoHeight,
    expandedCap,
    initialItemsCount,
    collapsedAutoHeight,
    collapsedHeight,
  ]);

  const needsScroll = expanded && (expandedAutoHeight || 0) > expandedCap;

  const showToggleBtn = useMemo(() => {
    if (!showToggle) return false;
    if (initialItemsCount) return itemsLength > initialItemsCount;

    return (expandedAutoHeight || 0) > collapsedHeight;
  }, [
    showToggle,
    initialItemsCount,
    itemsLength,
    expandedAutoHeight,
    collapsedHeight,
  ]);

  const handleToggle = () => {
    if (expanded && viewRef.current) {
      viewRef.current.scrollTop = 0; // при сворачивании поднимаем скролл к началу
    }
    setExpanded((p) => !p);
  };

  return (
    <Stack className={classNames(cls.Wrapper, {}, [className])}>
      <Stack
        className={classNames(cls.List, {
          [cls.Expanded]: expanded,
          [cls.Collapsed]:
            !expanded &&
            (initialItemsCount
              ? itemsLength > initialItemsCount
              : itemsLength > 1),
          [cls.Scrollable]: needsScroll,
        })}
        style={{
          maxHeight: `${visibleHeight}px`,
          paddingBottom: showToggleBtn ? '0' : '8px',
        }}
        gap={gap}
        ref={viewRef}
      >
        {initialItemsCount && !expanded ? slicedChildren : childrenArray}
      </Stack>

      <Stack
        className={cls.Measure}
        aria-hidden
        ref={measureCollapsedRef}
        style={{
          paddingBottom: showToggleBtn ? '0' : '8px',
        }}
      >
        {slicedChildren}
      </Stack>
      <Stack
        className={cls.Measure}
        aria-hidden
        ref={measureExpandedRef}
        style={{
          paddingBottom: showToggleBtn ? '0' : '8px',
        }}
      >
        {childrenArray}
      </Stack>

      {showToggleBtn && (
        <Button
          fullWidth
          variant='text'
          color='neutral'
          size='small'
          onClick={handleToggle}
        >
          {expanded ? (
            <Stack className={cls.ButtonText}>
              {buttonTextHide}
              <IconWrapper icon={ChevronUp} />
            </Stack>
          ) : (
            <Stack className={cls.ButtonText}>
              {buttonTextReveal}
              <IconWrapper icon={ChevronDown} />
            </Stack>
          )}
        </Button>
      )}
    </Stack>
  );
};
